import { put } from 'redux-saga/effects';
import { HistoryActions } from 'store/createHistoryReducer';
import {
  setFlowsheetMetadata,
  setProjectData,
} from 'store/documentMetadata/reducer';
import { setFileHasBeenEdited } from 'store/fileState/reducer';
import { editPrintProperties, setSettings } from 'store/settings/reducer';
import { setConnections, setUnitData } from 'store/units/reducer';
import { importFileState } from 'util/file';
import { setCurrentFile } from './reducer';
import type { CurrentFile, StorageUnit } from './types';
import { normalizeLeases, toIsoString } from './utils';

export const currentFileFromStorageUnit = (
  unit: StorageUnit,
  fallbackIsSynced = true,
): CurrentFile => {
  const normalized = normalizeLeases(unit);

  return {
    id: normalized.id,
    name: normalized.name,
    status: normalized.status,
    updatedAt: normalized.updatedAt,
    isSynced: normalized.isSynced ?? fallbackIsSynced,
    syncedAt: normalized.syncedAt ?? toIsoString(normalized.updatedAt),
    exclusiveLease: normalized.exclusiveLease,
    softLeases: normalized.softLeases ?? [],
    access: normalized.access,
  };
};

export function* setCurrentFileFromStorageUnit(
  unit: StorageUnit,
  fallbackIsSynced = true,
): Generator<unknown, void, unknown> {
  yield put(setCurrentFile(currentFileFromStorageUnit(unit, fallbackIsSynced)));
}

export function* hydrateDesignerFromStorageUnit(
  unit: StorageUnit,
): Generator<unknown, void, unknown> {
  if (unit.fileData === undefined) {
    return;
  }

  const importedState = importFileState(
    unit.name,
    unit.fileData,
    false,
    unit.flowsheetMetadata,
  );
  const {
    unitState,
    connectionState,
    settingState,
    documentMetadata,
    fileState,
  } = importedState;

  yield put(
    setUnitData({
      units: unitState.units,
      ports: unitState.ports,
      panels: unitState.panels,
      groups: unitState.groups,
      simulationResults: unitState.simulationResults,
      chamberAnalysisResults: unitState.chamberAnalysisResults,
    }),
  );
  yield put(setConnections(connectionState.connections));
  yield put(editPrintProperties(settingState.printProperties));
  yield put(setSettings({ ...settingState }));
  if (documentMetadata) {
    yield put(setProjectData(documentMetadata.projectData));
    if (documentMetadata.flowsheetMetadata) {
      yield put(setFlowsheetMetadata(documentMetadata.flowsheetMetadata));
    }
  }
  yield put(setFileHasBeenEdited(fileState?.fileHasBeenEdited ?? false));
  yield put(HistoryActions.clearHistory());
}
